const ED25519 = { name: "Ed25519" as const };

// Fixed ASN.1 prefixes (RFC 8410) — raw 32-byte key follows
const SPKI = Uint8Array.from([
  0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00,
]);
const PKCS8 = Uint8Array.from([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04,
  0x22, 0x04, 0x20,
]);

export type KeyPair = {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
  publicKeyB64: string;
  privateKeyB64: string;
};

export function b64(buf: ArrayBuffer | Uint8Array): string {
  const u = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]!);
  return btoa(s);
}

export function unb64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function cat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const o = new Uint8Array(a.length + b.length);
  o.set(a);
  o.set(b, a.length);
  return o;
}

export function spkiB64FromRaw(raw32: Uint8Array): string {
  return b64(cat(SPKI, raw32));
}

export function pkcs8B64FromSeed(seed32: Uint8Array): string {
  return b64(cat(PKCS8, seed32));
}

export function rawFromSpkiB64(spkiB64: string): Uint8Array {
  return unb64(spkiB64).slice(-32);
}

export function seedFromPkcs8B64(pkcs8B64: string): Uint8Array {
  return unb64(pkcs8B64).slice(-32);
}

export async function generateKeyPair(): Promise<KeyPair> {
  const pair = (await crypto.subtle.generateKey(ED25519, true, [
    "sign",
    "verify",
  ])) as CryptoKeyPair;
  const spki = await crypto.subtle.exportKey("spki", pair.publicKey);
  const pkcs8 = await crypto.subtle.exportKey("pkcs8", pair.privateKey);
  return {
    publicKey: pair.publicKey,
    privateKey: pair.privateKey,
    publicKeyB64: b64(spki),
    privateKeyB64: b64(pkcs8),
  };
}

export async function signMessage(privateKey: CryptoKey, message: string): Promise<string> {
  return b64(await crypto.subtle.sign(ED25519, privateKey, new TextEncoder().encode(message)));
}

export async function exportPrivateKey(privateKey: CryptoKey): Promise<string> {
  return b64(await crypto.subtle.exportKey("pkcs8", privateKey));
}

export async function importPrivateKey(base64String: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("pkcs8", unb64(base64String) as BufferSource, ED25519, true, [
    "sign",
  ]);
}

export async function importPublicKey(base64String: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("spki", unb64(base64String) as BufferSource, ED25519, true, [
    "verify",
  ]);
}
